import Amplify, { Auth } from '@aws-amplify/auth'
import { withAuthenticator } from '@aws-amplify/ui-react'
import { RouteComponentProps, Router } from '@reach/router'
import cx from 'classnames'
import { Link } from 'gatsby'
import { Fragment, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import OutsideClickHandler from 'react-outside-click-handler'
import '../../styles/dashboard.scss'
import ArchivedResultQueue from './ArchivedResultQueue'
import AnonymizeData from './AnonymizeData'
import Batch from './Batch'
import Biograd from './Biograd'
import { DashboardTitle, DashboardTitleProvider } from './DashboardTitleContext'
import DiscountCodeDetails from './DiscountCodes/DiscountCodeDetails'
import StaticDiscountCodes from './DiscountCodes/StaticDiscountCodes'
import DPD from './DPD'
import EmailQueue from './EmailQueue'
import GenerateDiscountCodes from './GenerateDiscountCodes'
import GenerateKitIds from './GenerateKitIds'
import IdGenerationBatches from './IdGenerationBatches'
import Kit from './Kit'
import Kits from './Kits'
import Navbar from './Navbar'
import NewSession from './NewSession'
import Order from './Order'
import Orders from './Orders'
import PHE from './PHE'
import Day2And8SalesCounts from './PHE/Day2And8SalesCounts'
import PhotoEscalationQueue from './PhotoEscalationQueue'
import PhotoVerificationQueue from './PhotoVerificationQueue'
import CaptureResults from './RapidTesting/CaptureResults'
import ChooseSession from './RapidTesting/ChooseSession'
import RapidSession from './RapidTesting/RapidSession'
import RapidSessions from './RapidTesting/RapidSessions'
import { RecoveryCertificate, RecoveryCertificates } from './RecoveryCertificate'
import Results from './Results'
import TDL from './TDL'
import ThirdParties from './ThirdParties'
import ThirdParty from './ThirdParty'
import { ToastProvider } from './Toast'
import { AbilityProvider, useAbilities } from './Usergroups'
import VideoAppointmentsPage from './Video/VideoAppointments'
import VideoPage from './Video/VideoPage'
import VideoQueuePage from './Video/VideoQueuePage'
import Vision from './Vision'
import VisionManifest from './VisionManifest'

Amplify.configure({
  Auth: {
    region: process.env.GATSBY_AUTH_REGION,
    userPoolId: process.env.GATSBY_AUTH_USER_POOL_ID,
    userPoolWebClientId: process.env.GATSBY_AUTH_USER_POOL_CLIENT_ID,
  },
})

const NotFound = (props: RouteComponentProps) => <h1>Not Found</h1>

const Home = (props: RouteComponentProps) => {
  return (
    <div className="container">
      <DashboardTitle title="Dashboard" />

      <p className="">Use the 'Menu' tab on the left to navigate</p>
    </div>
  )
}

const FullDashboard = ({ userGroups }: { userGroups: string[] }) => {
  const { abilities } = useAbilities()
  const [showMenu, setShowMenu] = useState(false)

  const NavLink = (props) => (
    <Link
      {...props}
      getProps={({ isCurrent }) => {
        // the object returned here is passed to the
        // anchor element's props
        return {
          className: isCurrent ? 'is-active' : undefined,
        }
      }}
      onClick={() => setShowMenu(false)}
    />
  )

  const menuSections = [
    {
      name: 'General',
      items: [
        abilities?.can('view', 'Dashboard/Kits') && Kits,
        abilities?.can('view', 'Dashboard/Results') && Results,
        abilities?.can('view', 'Dashboard/RecoveryCertificates') && RecoveryCertificates,
        abilities?.can('view', 'Dashboard/PhotoVerificationQueue') && PhotoVerificationQueue,
        abilities?.can('view', 'Dashboard/PhotoEscalationQueue') && PhotoEscalationQueue,
        abilities?.can('view', 'Dashboard/ThirdParties') && ThirdParties,
        abilities?.can('view', 'Dashboard/GenerateKitIds') && GenerateKitIds,
        abilities?.can('view', 'Dashboard/EmailQueue') && EmailQueue,
        abilities?.can('view', 'Dashboard/DiscountCodes/StaticDiscountCodes') && StaticDiscountCodes,
        abilities?.can('view', 'Dashboard/Video/VideoPage') && VideoPage,
        abilities?.can('view', 'Dashboard/Video/VideoQueuePage') && VideoQueuePage,
        abilities?.can('view', 'Dashboard/Video/VideoAppointments') && VideoAppointmentsPage,
        abilities?.can('view', 'Dashboard/AnonymizeData') && AnonymizeData,
      ],
    },
    {
      name: 'Rapid Testing',
      items: [
        abilities?.can('view', 'Dashboard/RapidTesting/RapidSessions') && RapidSessions,
        abilities?.can('view', 'Dashboard/RapidTesting/CaptureResults') && CaptureResults,
      ],
    },
    {
      name: 'Integrations',
      items: [
        abilities?.can('view', 'Dashboard/Vision') && Vision,
        abilities?.can('view', 'Dashboard/PHE') && PHE,
        abilities?.can('view', 'Dashboard/Biograd') && Biograd,
        abilities?.can('view', 'Dashboard/DPD') && DPD,
        abilities?.can('view', 'Dashboard/TDL') && TDL,
      ],
    },
  ]
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item),
    }))
    .filter((section) => section.items.length > 0)

  if (!abilities) {
    return <p>Loading dashboard...</p>
  }

  return (
    <>
      <header className="mb-4">
        <Navbar />
      </header>

      <div className="main is-relative">
        <OutsideClickHandler onOutsideClick={() => setShowMenu(false)}>
          <div
            className={cx('dashboard-menu px-3 py-3', {
              'dashboard-menu_is-visible': showMenu,
            })}
          >
            <aside className="menu has-text-primary-light">
              <button className="dashboard-menu_trigger" onClick={() => setShowMenu((prev) => !prev)}>
                <div>Menu</div>
              </button>

              <ul className="menu-list">
                <li>
                  <NavLink to="/dashboard">Dashboard</NavLink>
                </li>
              </ul>
              {menuSections.map((section) => (
                <Fragment key={section.name}>
                  <p className="menu-label">{section.name}</p>

                  <ul className="menu-list">
                    {section.items.map((item) => (
                      <li key={item.name}>
                        <NavLink to={item.path ? `/dashboard${item.path}` : '/TODO'}>
                          {item.displayName || item.name}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </Fragment>
              ))}
            </aside>
          </div>
        </OutsideClickHandler>

        <Router basepath="/dashboard">
          {abilities?.can('view', 'Dashboard/Orders') && <Orders path="/orders" />}

          {abilities?.can('view', 'Dashboard/Order') && <Order path="/order/:orderId" />}

          {abilities?.can('view', 'Dashboard/Kits') && <Kits path="/kits" />}

          {abilities?.can('view', 'Dashboard/Kit') && <Kit path="/kit/:kitId" />}

          {abilities?.can('view', 'Dashboard/RecoveryCertificate') && (
            <RecoveryCertificate path="/recovery-certificate/:orderId" />
          )}

          {abilities?.can('view', 'Dashboard/RecoveryCertificates') && (
            <>
              <RecoveryCertificates path="/recovery-certificates/:orderId" />
              <RecoveryCertificates path="/recovery-certificates" />
            </>
          )}

          {abilities?.can('view', 'Dashboard/GenerateKitIds') && <GenerateKitIds path="/generate-kit-ids" />}

          {abilities?.can('view', 'Dashboard/GenerateDiscountCodes') && (
            <GenerateDiscountCodes path="/generate-discount-codes" />
          )}

          {abilities?.can('view', 'Dashboard/Vision') && <Vision path="/vision" />}

          {abilities?.can('view', 'Dashboard/VisionManifest') && <VisionManifest path="/vision/manifest/:manifestId" />}

          {abilities?.can('view', 'Dashboard/Batch') && <Batch path="/batch/:batchId" />}

          {abilities?.can('view', 'Dashboard/Results') && <Results path="/results" />}

          {abilities?.can('view', 'Dashboard/EmailQueue') && <EmailQueue path="/email-queue" />}

          {abilities?.can('view', 'Dashboard/ThirdParty') && <ThirdParty path="/third-party/:id" />}

          {abilities?.can('view', 'Dashboard/ThirdParties') && <ThirdParties path="/third-parties" />}

          {abilities?.can('view', 'Dashboard/RapidTesting/CaptureResults') && (
            <CaptureResults path="/rapid-testing/capture-results" />
          )}

          {abilities?.can('view', 'Dashboard/RapidTesting/ChooseSession') && (
            <ChooseSession path="/rapid-testing/choose-session" />
          )}

          {abilities?.can('view', 'Dashboard/RapidTesting/RapidSession') && (
            <RapidSession path="/rapid-testing/rapid-session/:sessionId" />
          )}

          {abilities?.can('view', 'Dashboard/RapidTesting/RapidSessions') && (
            <RapidSessions path="/rapid-testing/rapid-sessions" />
          )}

          {abilities?.can('view', 'Dashboard/NewSession') && (
            <NewSession path="/third-party/:thirdPartyId/sessions/new" />
          )}

          {abilities?.can('view', 'Dashboard/DiscountCodes/DiscountCodeDetails') && (
            <DiscountCodeDetails path="/third-party/:thirdPartyId/discount-code/:discountCode" />
          )}

          {abilities?.can('view', 'Dashboard/PHE') && <PHE path="/phe" />}

          {abilities?.can('view', 'Dashboard/PHE/Day2And8SalesCounts') && (
            <Day2And8SalesCounts path="/phe/day2and8salescounts" />
          )}

          {abilities?.can('view', 'Dashboard/IdGenerationBatches') && (
            <IdGenerationBatches path="/id-generation-batches" />
          )}

          {abilities?.can('view', 'Dashboard/PhotoVerificationQueue') && (
            <PhotoVerificationQueue path="/photo-verifications" />
          )}

          {abilities?.can('view', 'Dashboard/PhotoEscalationQueue') && (
            <PhotoEscalationQueue path="/escalated-photo-verifications" />
          )}

          {abilities?.can('view', 'Dashboard/DiscountCodes/StaticDiscountCodes') && (
            <StaticDiscountCodes path="/static-discount-codes" />
          )}

          {abilities?.can('view', 'Dashboard/Biograd') && <Biograd path="/biograd" />}

          {abilities?.can('view', 'Dashboard/DPD') && <DPD path="/dpd" />}

          {abilities?.can('view', 'Dashboard/TDL') && <TDL path="/tdl" />}

          {abilities?.can('view', 'Dashboard/ArchivedResultQueue') && <ArchivedResultQueue path="/archived-results" />}

          {abilities?.can('view', 'Dashboard/Video/VideoPage') && <VideoPage path={VideoPage.path} />}

          {abilities?.can('view', 'Dashboard/Video/VideoQueuePage') && <VideoQueuePage path={VideoQueuePage.path} />}

          {abilities?.can('view', 'Dashboard/Video/VideoAppointments') && (
            <VideoAppointmentsPage path={VideoAppointmentsPage.path} />
          )}

          {abilities?.can('view', 'Dashboard/AnonymizeData') && <AnonymizeData path="/anonymize-data" />}

          {abilities?.can('view', 'Dashboard/Home') && <Home path="/" />}

          <NotFound default />
        </Router>
      </div>

      <footer className="footer">
        <a href="/">C19 Testing</a>
      </footer>
    </>
  )
}

const Dashboard = () => {
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(true)
  const [groups, setGroups] = useState([])

  useEffect(() => {
    const fetchUser = async () => {
      const user = await Auth.currentSession()
      setGroups(user.getAccessToken().payload['cognito:groups'])
      setLoading(false)
    }

    fetchUser()
  }, [])

  if (loading) {
    return <p>Loading...</p>
  }

  return (
    <AbilityProvider>
      <ToastProvider>
        <DashboardTitleProvider value={{ setTitle }}>
          <Helmet title={title} titleTemplate="%s | C19 Admin" />

          <FullDashboard userGroups={groups} />
        </DashboardTitleProvider>
      </ToastProvider>
    </AbilityProvider>
  )
}

export default withAuthenticator(Dashboard)
